import { SdkContext } from '../internal'
import { listItems } from '@/lib/utils'
import type {
  HostPageData,
  HostSearchParams,
  HostSearchResponse,
  PendingListing,
  SchedulingConfig
} from '@/types/scheduling'

const clean = (query: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== ''))

/** The payload whether the transport kept the `{ data, pagination }` envelope or not. */
const payload = <T>(result: unknown): T => {
  const r = result as { data?: unknown } | null
  return (r && typeof r === 'object' && 'data' in r && r.data !== null && typeof r.data === 'object' ? r.data : result) as T
}

/**
 * F-80: the Agendamento layer over the booking core (`sdk.bookings`): offer rules, people search, host page and
 * the approval of members' offers. Paths are written by hand (F-03).
 */
export class SchedulingResource {
  constructor(private ctx: SdkContext) {}

  async config(spaceId: string): Promise<SchedulingConfig> {
    return this.ctx.call({ httpMethod: 'GET', path: '/scheduling/config' }, { query: { spaceId } })
  }

  async pending(spaceId: string): Promise<PendingListing[]> {
    return listItems<PendingListing>(await this.ctx.raw({ httpMethod: 'GET', path: '/scheduling/pending' }, { query: { spaceId } }))
  }

  /** Owner and admin. Withdrawing keeps the bookings already made. */
  async setListing(resourceId: string, listed: boolean): Promise<{ id: string; listed: boolean; offererStands: boolean }> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/scheduling/resources/{resourceId}/listing', hasBody: true }, { resourceId, body: { listed } })
  }

  async searchHosts(params: HostSearchParams): Promise<HostSearchResponse> {
    const result = await this.ctx.raw<{ pagination?: HostSearchResponse['pagination'] } | null>({ httpMethod: 'GET', path: '/scheduling/hosts' }, { query: clean(params as unknown as Record<string, unknown>) })
    const data = payload<Partial<HostSearchResponse>>(result)
    return {
      hosts: data.hosts ?? [],
      nextSlots: data.nextSlots ?? [],
      balance: data.balance ?? 0,
      timeZone: data.timeZone ?? '',
      pagination: result?.pagination ?? data.pagination ?? null
    }
  }

  async hostPage(memberId: string, params: { spaceId: string; date?: string; days?: number; durationMinutes?: number }): Promise<HostPageData> {
    return payload<HostPageData>(await this.ctx.raw({ httpMethod: 'GET', path: '/scheduling/hosts/{memberId}' }, { memberId, query: clean(params) }))
  }
}
